import { t } from "@/lib/i18n";
import { useState } from "react";
import { CalendarPlus, Loader2, Download, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { extractDependency, formatAppt } from "@/lib/cases";
import { downloadIcs, googleCalendarUrl } from "@/lib/calendar";

const REMINDERS = [
  { v: 1440 },
  { v: 120 },
  { v: 30 },
];

const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 86400000);
  if (!iso) d.setHours(9, 0, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export const CalendarActions = ({ c }) => (
  <div className="grid grid-cols-2 gap-2">
    <button data-testid={`calendar-ics-${c.folio}`} onClick={() => downloadIcs(c)} className="flex items-center justify-center gap-1.5 rounded-xl bg-ac-strong px-3 py-2.5 text-xs font-semibold text-white hover:bg-ac-strong-hover">
      <Download className="h-4 w-4" /> {t("appt.ics")}
    </button>
    <a data-testid={`calendar-google-${c.folio}`} href={googleCalendarUrl(c)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 rounded-xl border border-ac-line px-3 py-2.5 text-xs font-semibold text-ac-ink hover:bg-ac-surface2">
      <CalendarCheck className="h-4 w-4" /> {t("appt.google")}
    </a>
  </div>
);

export const AppointmentDialog = ({ c, open, onClose, onSave }) => {
  const [when, setWhen] = useState(toLocalInput(c.appointment?.at));
  const [place, setPlace] = useState(c.appointment?.place || extractDependency(c.answer) || "");
  const [reminder, setReminder] = useState(c.appointment?.reminder_minutes || 1440);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setSaved(await onSave(c.folio, { appointment_at: new Date(when).toISOString(), place, reminder_minutes: Number(reminder) }));
      toast.success(t("appt.saved"));
    } catch {
      toast.error(t("appt.err"));
    }
    setBusy(false);
  };

  const close = () => {
    setSaved(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="appointment-dialog" className="max-w-md rounded-2xl font-body">
        <DialogHeader>
          <span className="mb-2 grid h-11 w-11 place-items-center rounded-xl bg-ac-accent-soft2 text-ac-accent-ink"><CalendarPlus className="h-5 w-5" /></span>
          <DialogTitle className="font-display text-xl text-ac-ink">{saved ? t("appt.done") : t("appt.title")}</DialogTitle>
          <DialogDescription>
            {saved ? t("appt.doneDesc", { when: formatAppt(saved.appointment.at) }) : t("appt.desc", { folio: c.folio })}
          </DialogDescription>
        </DialogHeader>
        {saved ? (
          <CalendarActions c={saved} />
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="block text-sm font-medium text-ac-text">
              {t("appt.when")}
              <input data-testid="appointment-datetime-input" type="datetime-local" required value={when} onChange={(e) => setWhen(e.target.value)} className="mt-1 h-11 w-full rounded-xl border border-ac-line px-3 font-mono text-sm outline-none focus:border-ac-primary focus:ring-4 focus:ring-ac-primary-ring" />
            </label>
            <label className="block text-sm font-medium text-ac-text">
              {t("appt.place")}
              <input data-testid="appointment-place-input" value={place} onChange={(e) => setPlace(e.target.value)} placeholder={t("appt.placePh")} className="mt-1 h-11 w-full rounded-xl border border-ac-line px-3 text-sm outline-none focus:border-ac-primary focus:ring-4 focus:ring-ac-primary-ring" />
            </label>
            <div>
              <p className="text-sm font-medium text-ac-text">{t("appt.reminder")}</p>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {REMINDERS.map((r) => (
                  <button type="button" key={r.v} data-testid={`appointment-reminder-${r.v}`} onClick={() => setReminder(r.v)} className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${Number(reminder) === r.v ? "border-ac-primary bg-ac-primary-soft text-ac-primary-ink" : "border-ac-line text-ac-muted hover:bg-ac-surface2"}`}>
                    {t(`rem.${r.v}`)}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="appointment-save-button" disabled={busy || !when} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-ac-accent font-semibold text-ac-ink transition-colors hover:brightness-95 disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />} {t("appt.save")}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
